<template>
  <div class="tianyi-management">
    <el-tabs v-model="activeTab">
      <!-- 待上传资源 -->
      <el-tab-pane label="待上传资源" name="pending">
        <div class="filter-bar">
          <el-button type="primary" @click="loadPendingResources">刷新列表</el-button>
          <el-button type="success" @click="handleTriggerAutoUpload" :loading="triggerLoading">
            {{ autoUploadStatus.processing ? '处理中...' : '立即处理' }}
          </el-button>
          <span class="total-info">共 {{ pendingTotal }} 个待上传资源</span>
          <el-tag 
            :type="autoUploadStatus.running ? 'success' : 'info'" 
            style="margin-left: 10px"
          >
            自动上传: {{ autoUploadStatus.running ? '运行中' : '已停止' }}
          </el-tag>
          <span v-if="autoUploadStatus.currentTask" class="current-task">
            正在处理: {{ autoUploadStatus.currentTask.title }}
          </span>
        </div>
        
        <el-table :data="pendingResources" v-loading="pendingLoading" border>
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="title" label="游戏名称" min-width="200">
            <template slot-scope="scope">
              <span>{{ scope.row.title }}</span>
              <el-tag 
                v-if="scope.row.storage_type === 'tianyi' && scope.row.upload_status === 'pending'" 
                size="mini" 
                type="danger"
                style="margin-left: 8px"
              >需更新</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="category" label="分类" width="100" />
          <el-table-column prop="storage_type" label="存储类型" width="100">
            <template slot-scope="scope">
              <el-tag :type="getStorageTypeTag(scope.row.storage_type)">
                {{ getStorageTypeLabel(scope.row.storage_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="upload_status" label="上传状态" width="120">
            <template slot-scope="scope">
              <el-tag :type="getUploadStatusTag(scope.row.upload_status)">
                {{ getUploadStatusLabel(scope.row.upload_status) }}
              </el-tag>
              <el-tooltip 
                v-if="scope.row.upload_status === 'failed' && scope.row.retry_count > 0"
                :content="scope.row.last_error || '未知错误'"
                placement="top"
              >
                <span class="retry-count">({{ scope.row.retry_count }}/3)</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button size="mini" type="success" @click="handleAutoUpload(scope.row)" :loading="scope.row.autoUploading">
                  自动上传
                </el-button>
                <el-button size="mini" type="primary" @click="openBindDialog(scope.row)">
                  绑定
                </el-button>
                <el-button 
                  v-if="scope.row.upload_status === 'uploading'" 
                  size="mini" 
                  type="info" 
                  @click="showTaskProgress(scope.row)"
                >
                  进度
                </el-button>
                <el-button 
                  v-if="scope.row.upload_status === 'uploading'" 
                  size="mini" 
                  type="warning" 
                  @click="resetUploadStatus(scope.row)"
                >
                  重置
                </el-button>
                <el-button 
                  v-if="scope.row.upload_status === 'failed' && scope.row.retry_count >= 3" 
                  size="mini" 
                  type="warning" 
                  @click="retryUpload(scope.row)"
                >
                  重试
                </el-button>
                <el-button 
                  size="mini" 
                  type="danger" 
                  @click="deleteResource(scope.row)"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        
        <el-pagination
          @current-change="handlePendingPageChange"
          :current-page="pendingPage"
          :page-size="20"
          :total="pendingTotal"
          layout="total, prev, pager, next"
        />
      </el-tab-pane>

      <!-- 天翼云文件浏览 -->
      <el-tab-pane label="天翼云文件" name="files">
        <div class="filter-bar">
          <el-select v-model="selectedAccountId" placeholder="选择账号" @change="loadTianyiFiles">
            <el-option
              v-for="account in tianyiAccounts"
              :key="account.id"
              :label="account.name"
              :value="account.id"
            />
          </el-select>
          <el-button type="primary" @click="loadTianyiFiles" :disabled="!selectedAccountId">
            刷新文件
          </el-button>
          <el-button @click="goToParentFolder" :disabled="!currentFolderId || currentFolderId === '-11'">
            返回上级
          </el-button>
        </div>

        <el-table :data="tianyiFiles" v-loading="filesLoading" border @row-dblclick="handleFileDoubleClick">
          <el-table-column prop="name" label="文件名" min-width="300">
            <template slot-scope="scope">
              <i :class="scope.row.isFolder ? 'el-icon-folder' : 'el-icon-document'" />
              {{ scope.row.name }}
            </template>
          </el-table-column>
          <el-table-column prop="size" label="大小" width="120">
            <template slot-scope="scope">
              {{ scope.row.isFolder ? '-' : formatFileSize(scope.row.size) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template slot-scope="scope">
              <template v-if="!scope.row.isFolder">
                <el-button size="mini" @click="getFileLink(scope.row)">获取直链</el-button>
              </template>
              <template v-else>
                <el-button size="mini" @click="enterFolder(scope.row)">进入</el-button>
              </template>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- 手动绑定 -->
      <el-tab-pane label="手动绑定" name="manual">
        <div class="filter-bar">
          <el-input 
            v-model="searchKeyword" 
            placeholder="输入游戏名称搜索" 
            style="width: 300px"
            clearable
            @keyup.enter.native="searchGames"
          >
            <el-button slot="append" icon="el-icon-search" @click="searchGames">搜索</el-button>
          </el-input>
          <span class="total-info" v-if="searchTotal > 0">找到 {{ searchTotal }} 个游戏</span>
        </div>
        
        <el-table :data="searchResults" v-loading="searchLoading" border>
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="title" label="游戏名称" min-width="200" />
          <el-table-column prop="category" label="分类" width="100" />
          <el-table-column prop="storage_type" label="存储类型" width="100">
            <template slot-scope="scope">
              <el-tag :type="getStorageTypeTag(scope.row.storage_type)">
                {{ getStorageTypeLabel(scope.row.storage_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150">
            <template slot-scope="scope">
              <el-button 
                size="mini" 
                :type="scope.row.storage_type === 'tianyi' ? 'warning' : 'primary'" 
                @click="openBindDialog(scope.row)"
              >
                {{ scope.row.storage_type === 'tianyi' ? '重新绑定' : '绑定文件' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <el-pagination
          v-if="searchTotal > 0"
          @current-change="handleSearchPageChange"
          :current-page="searchPage"
          :page-size="20"
          :total="searchTotal"
          layout="total, prev, pager, next"
          style="margin-top: 20px"
        />
      </el-tab-pane>

      <!-- 已绑定资源 -->
      <el-tab-pane label="已绑定资源" name="bound">
        <div class="filter-bar">
          <el-button type="primary" @click="loadBoundResources">刷新列表</el-button>
          <span class="total-info">共 {{ boundTotal }} 个已绑定资源</span>
        </div>
        
        <el-table :data="boundResources" v-loading="boundLoading" border>
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="title" label="游戏名称" min-width="200" />
          <el-table-column prop="category" label="分类" width="100" />
          <el-table-column label="天翼云信息" width="200">
            <template slot-scope="scope">
              <div class="tianyi-info">
                <div>账号ID: {{ scope.row.tianyi_account_id }}</div>
                <div v-if="scope.row.tianyi_share_code">
                  分享码: {{ scope.row.tianyi_share_code }}
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" @click="testDownloadLink(scope.row)">
                测试下载
              </el-button>
              <el-button size="mini" @click="copyShareUrl(scope.row)">
                复制链接
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <el-pagination
          @current-change="handleBoundPageChange"
          :current-page="boundPage"
          :page-size="20"
          :total="boundTotal"
          layout="total, prev, pager, next"
        />
      </el-tab-pane>

      <!-- 服务状态 -->
      <el-tab-pane label="服务状态" name="status">
        <el-card>
          <div slot="header">天翼云服务状态</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="服务状态">
              <el-tag :type="serviceStatus.ok ? 'success' : 'danger'">
                {{ serviceStatus.ok ? '正常' : '异常' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="检查时间">
              {{ serviceStatus.checkTime || '-' }}
            </el-descriptions-item>
          </el-descriptions>
          <el-button type="primary" @click="checkServiceHealth" style="margin-top: 20px">
            检查服务状态
          </el-button>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 绑定文件对话框（支持多文件） -->
    <el-dialog title="绑定天翼云文件" :visible.sync="bindDialogVisible" width="1000px" top="5vh">
      <div class="bind-dialog-content">
        <!-- 左侧：资源信息和已选文件列表 -->
        <div class="bind-info-panel">
          <h4>待绑定资源</h4>
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item label="资源ID">{{ bindForm.resourceId }}</el-descriptions-item>
            <el-descriptions-item label="资源名称">{{ bindForm.resourceTitle }}</el-descriptions-item>
          </el-descriptions>
          
          <h4 style="margin-top: 20px">
            已选择文件 
            <el-tag size="mini" type="success" v-if="selectedFiles.length > 0">{{ selectedFiles.length }}个</el-tag>
          </h4>
          
          <div class="selected-files-list" v-if="selectedFiles.length > 0">
            <div 
              v-for="(file, index) in selectedFiles" 
              :key="file.fileId" 
              class="selected-file-item"
            >
              <div class="file-index">{{ index + 1 }}</div>
              <div class="file-info">
                <div class="file-label-input">
                  <el-input 
                    v-model="file.label" 
                    size="mini"
                    placeholder="自定义标签（可选）"
                    clearable
                    @input="updateFileLabel(index, $event)"
                  />
                </div>
                <div class="file-name" :title="file.fileName">{{ file.fileName }}</div>
                <div class="file-size">{{ formatFileSize(file.fileSize) }}</div>
              </div>
              <el-button 
                type="text" 
                size="mini" 
                icon="el-icon-close"
                @click="removeSelectedFile(index)"
              />
            </div>
            <el-button 
              type="text" 
              size="mini" 
              icon="el-icon-delete"
              @click="clearSelectedFiles"
              style="margin-top: 8px"
            >清空全部</el-button>
          </div>
          <el-empty v-else description="请在右侧选择文件" :image-size="60" />
        </div>
        
        <!-- 右侧：文件浏览器 -->
        <div class="bind-file-browser">
          <div class="browser-toolbar">
            <el-select v-model="bindForm.accountId" placeholder="选择账号" size="small" @change="loadBindFiles">
              <el-option
                v-for="account in tianyiAccounts"
                :key="account.id"
                :label="account.name"
                :value="account.id"
              />
            </el-select>
            <el-button size="small" @click="loadBindFiles" :disabled="!bindForm.accountId">刷新</el-button>
            <el-button size="small" @click="goToBindParentFolder" :disabled="bindFolderId === '-11'">返回上级</el-button>
          </div>
          
          <!-- 文件搜索框 -->
          <div class="file-search-bar">
            <el-input 
              v-model="bindFileSearchKeyword" 
              placeholder="搜索文件名..." 
              size="small"
              clearable
              prefix-icon="el-icon-search"
              @input="filterBindFiles"
            />
          </div>
          
          <el-table 
            :data="filteredBindFiles" 
            v-loading="bindFilesLoading" 
            border 
            size="small"
            height="320"
            highlight-current-row
            @row-dblclick="handleBindFileDoubleClick"
          >
            <el-table-column prop="name" label="文件名" min-width="200">
              <template slot-scope="scope">
                <i :class="scope.row.isFolder ? 'el-icon-folder' : 'el-icon-document'" />
                {{ scope.row.name }}
                <el-tag size="mini" type="success" v-if="isFileSelected(scope.row)">已选</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="size" label="大小" width="100">
              <template slot-scope="scope">
                {{ scope.row.isFolder ? '-' : formatFileSize(scope.row.size) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100">
              <template slot-scope="scope">
                <el-button 
                  v-if="scope.row.isFolder" 
                  size="mini" 
                  @click="enterBindFolder(scope.row)"
                >进入</el-button>
                <el-button 
                  v-else-if="isFileSelected(scope.row)"
                  size="mini" 
                  type="danger"
                  @click="toggleSelectFile(scope.row)"
                >取消</el-button>
                <el-button 
                  v-else 
                  size="mini" 
                  type="primary"
                  @click="toggleSelectFile(scope.row)"
                >选择</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      
      <span slot="footer">
        <el-button @click="bindDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBind" :loading="bindLoading" :disabled="selectedFiles.length === 0">
          确定绑定 ({{ selectedFiles.length }}个文件)
        </el-button>
      </span>
    </el-dialog>

    <!-- 直链结果对话框 -->
    <el-dialog title="文件直链" :visible.sync="linkDialogVisible" width="650px">
      <el-form label-width="100px">
        <el-form-item label="永久直链">
          <el-input v-model="currentShareUrl" readonly>
            <template slot="append">
              <el-button @click="copyLink(currentShareUrl)">复制</el-button>
            </template>
          </el-input>
          <div class="link-tip">推荐使用，永久有效，每次访问自动获取最新下载地址</div>
        </el-form-item>
        <el-form-item label="临时直链">
          <el-input v-model="currentLink" readonly>
            <template slot="append">
              <el-button @click="copyLink(currentLink)">复制</el-button>
            </template>
          </el-input>
          <div class="link-tip">天翼云原始地址，有时效性，可能会过期</div>
        </el-form-item>
        <el-form-item label="分享码">
          <el-input v-model="currentShareCode" readonly style="width: 200px">
            <template slot="append">
              <el-button @click="copyLink(currentShareCode)">复制</el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <div style="margin-top: 10px; text-align: right">
        <el-button type="primary" @click="openLink(currentShareUrl)">打开永久链接</el-button>
        <el-button @click="linkDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 自动上传对话框 -->
    <el-dialog title="自动上传到天翼云" :visible.sync="autoUploadDialogVisible" width="800px" top="5vh">
      <div class="auto-upload-content">
        <!-- 资源信息 -->
        <el-descriptions :column="1" border size="small" style="margin-bottom: 20px">
          <el-descriptions-item label="资源名称">{{ autoUploadForm.resourceTitle }}</el-descriptions-item>
          <el-descriptions-item label="资源ID">{{ autoUploadForm.resourceId }}</el-descriptions-item>
        </el-descriptions>
        
        <!-- 选择账号和文件夹 -->
        <el-form label-width="100px">
          <el-form-item label="选择账号" required>
            <el-select v-model="autoUploadForm.accountId" placeholder="选择天翼云账号" @change="loadAutoUploadFolders">
              <el-option
                v-for="account in tianyiAccounts"
                :key="account.id"
                :label="account.name"
                :value="account.id"
              />
            </el-select>
          </el-form-item>
          
          <el-form-item label="保存位置">
            <div class="folder-browser">
              <div class="folder-path">
                <el-breadcrumb separator="/">
                  <el-breadcrumb-item @click.native="goToAutoUploadRoot" style="cursor: pointer">根目录</el-breadcrumb-item>
                  <el-breadcrumb-item v-for="folder in autoUploadFolderPath" :key="folder.id">
                    {{ folder.name }}
                  </el-breadcrumb-item>
                </el-breadcrumb>
              </div>
              <el-button size="small" @click="goToAutoUploadParent" :disabled="autoUploadFolderId === '-11'">
                返回上级
              </el-button>
            </div>
            
            <el-table 
              :data="autoUploadFolders" 
              v-loading="autoUploadFoldersLoading" 
              border 
              size="small"
              height="250"
              @row-dblclick="enterAutoUploadFolder"
            >
              <el-table-column prop="name" label="文件夹名称">
                <template slot-scope="scope">
                  <i class="el-icon-folder" style="color: #f0c040; margin-right: 5px"></i>
                  {{ scope.row.name }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100">
                <template slot-scope="scope">
                  <el-button size="mini" @click="enterAutoUploadFolder(scope.row)">进入</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="folder-tip">当前保存位置：{{ autoUploadFolderPath.length > 0 ? autoUploadFolderPath[autoUploadFolderPath.length - 1].name : '根目录' }}</div>
          </el-form-item>
        </el-form>
      </div>
      
      <span slot="footer">
        <el-button @click="autoUploadDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAutoUpload" :loading="autoUploadLoading" :disabled="!autoUploadForm.accountId">
          开始上传
        </el-button>
      </span>
    </el-dialog>

    <!-- 任务进度对话框 -->
    <el-dialog title="上传任务进度" :visible.sync="progressDialogVisible" width="600px">
      <div v-loading="progressLoading">
        <el-descriptions :column="1" border size="small" style="margin-bottom: 15px">
          <el-descriptions-item label="资源名称">{{ progressData.resourceTitle }}</el-descriptions-item>
          <el-descriptions-item label="整体状态">
            <el-tag :type="getUploadStatusTag(progressData.uploadStatus)">
              {{ getUploadStatusLabel(progressData.uploadStatus) }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>
        
        <div v-if="progressData.tasks && progressData.tasks.length > 0">
          <div v-for="task in progressData.tasks" :key="task.id" class="task-progress-item">
            <div class="task-header">
              <span class="task-name">{{ task.fileName || '未知文件' }}</span>
              <el-tag size="mini" :type="getTaskStatusTag(task.status)">{{ getTaskStatusLabel(task.status) }}</el-tag>
            </div>
            <el-progress 
              :percentage="task.progress || 0" 
              :status="task.status === 'completed' ? 'success' : (task.status === 'failed' ? 'exception' : null)"
            />
            <div class="task-info">
              <span v-if="task.downloadedSize">已下载: {{ formatFileSize(task.downloadedSize) }}</span>
              <span v-if="task.totalSize"> / {{ formatFileSize(task.totalSize) }}</span>
              <span v-if="task.speed" style="margin-left: 10px">速度: {{ formatFileSize(task.speed) }}/s</span>
              <span v-if="task.error" style="color: #f56c6c; margin-left: 10px">{{ task.error }}</span>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无任务信息" />
      </div>
      <span slot="footer">
        <el-button @click="refreshTaskProgress" :loading="progressLoading">刷新进度</el-button>
        <el-button type="primary" @click="progressDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  tianyiHealthCheck,
  getTianyiAccounts,
  listTianyiFiles,
  getTianyiDownloadLink,
  bindTianyiFile,
  getPendingResources,
  getBoundResources,
  getUnifiedDownloadLink,
  searchAllResources,
  autoUploadToTianyi,
  getTianyiTaskStatus,
  resetResourceUploadStatus,
  getResourceTaskProgress,
  getAutoUploadStatus,
  triggerAutoUpload,
  removeFromPendingList
} from '@/api'

export default {
  name: 'TianyiManagement',
  data() {
    return {
      activeTab: 'pending',
      // 待上传资源
      pendingResources: [],
      pendingLoading: false,
      pendingPage: 1,
      pendingTotal: 0,
      // 已绑定资源
      boundResources: [],
      boundLoading: false,
      boundPage: 1,
      boundTotal: 0,
      // 手动绑定搜索
      searchKeyword: '',
      searchResults: [],
      searchLoading: false,
      searchPage: 1,
      searchTotal: 0,
      // 天翼云文件
      tianyiAccounts: [],
      selectedAccountId: null,
      tianyiFiles: [],
      filesLoading: false,
      currentFolderId: '-11',
      folderHistory: [],
      // 服务状态
      serviceStatus: { ok: false, checkTime: null },
      // 绑定对话框
      bindDialogVisible: false,
      bindLoading: false,
      bindForm: {
        resourceId: null,
        resourceTitle: '',
        accountId: null
      },
      // 已选择的文件列表（支持多文件）
      selectedFiles: [],
      // 绑定对话框内的文件浏览
      bindFiles: [],
      bindFilesLoading: false,
      bindFolderId: '-11',
      bindFolderHistory: [],
      bindFileSearchKeyword: '', // 文件搜索关键词
      // 直链对话框
      linkDialogVisible: false,
      currentLink: '',
      currentShareUrl: '',
      currentShareCode: '',
      // 自动上传对话框
      autoUploadDialogVisible: false,
      autoUploadLoading: false,
      autoUploadForm: {
        resourceId: null,
        resourceTitle: '',
        accountId: null
      },
      autoUploadFolders: [],
      autoUploadFoldersLoading: false,
      autoUploadFolderId: '-11',
      autoUploadFolderHistory: [],
      autoUploadFolderPath: [],
      // 进度对话框
      progressDialogVisible: false,
      progressLoading: false,
      progressResourceId: null,
      progressData: {
        resourceTitle: '',
        uploadStatus: '',
        tasks: []
      },
      // 自动上传服务状态
      autoUploadStatus: {
        running: false,
        processing: false,
        currentTask: null
      },
      triggerLoading: false
    }
  },
  computed: {
    // 过滤后的绑定文件列表
    filteredBindFiles() {
      if (!this.bindFileSearchKeyword || !this.bindFileSearchKeyword.trim()) {
        return this.bindFiles
      }
      const keyword = this.bindFileSearchKeyword.toLowerCase().trim()
      return this.bindFiles.filter(file => 
        file.name && file.name.toLowerCase().includes(keyword)
      )
    }
  },
  mounted() {
    this.loadPendingResources()
    this.loadBoundResources()
    this.loadTianyiAccounts()
    this.checkServiceHealth()
  },
  methods: {
    // 加载待上传资源
    async loadPendingResources() {
      this.pendingLoading = true
      try {
        const res = await getPendingResources({ page: this.pendingPage, pageSize: 20 })
        if (res.success) {
          this.pendingResources = res.data.data
          this.pendingTotal = res.data.pagination.total
        }
      } catch (e) {
        this.$message.error('加载失败')
      }
      this.pendingLoading = false
    },
    handlePendingPageChange(page) {
      this.pendingPage = page
      this.loadPendingResources()
    },
    // 加载已绑定资源
    async loadBoundResources() {
      this.boundLoading = true
      try {
        const res = await getBoundResources({ page: this.boundPage, pageSize: 20 })
        if (res.success) {
          this.boundResources = res.data.data
          this.boundTotal = res.data.pagination.total
        }
      } catch (e) {
        this.$message.error('加载失败')
      }
      this.boundLoading = false
    },
    handleBoundPageChange(page) {
      this.boundPage = page
      this.loadBoundResources()
    },
    // 搜索游戏
    async searchGames() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      this.searchLoading = true
      this.searchPage = 1
      try {
        const res = await searchAllResources({ 
          title: this.searchKeyword, 
          page: this.searchPage, 
          pageSize: 20 
        })
        if (res.success) {
          this.searchResults = res.data.data
          this.searchTotal = res.data.pagination.total
        }
      } catch (e) {
        this.$message.error('搜索失败')
      }
      this.searchLoading = false
    },
    handleSearchPageChange(page) {
      this.searchPage = page
      this.searchGamesWithPage()
    },
    async searchGamesWithPage() {
      this.searchLoading = true
      try {
        const res = await searchAllResources({ 
          title: this.searchKeyword, 
          page: this.searchPage, 
          pageSize: 20 
        })
        if (res.success) {
          this.searchResults = res.data.data
          this.searchTotal = res.data.pagination.total
        }
      } catch (e) {
        this.$message.error('搜索失败')
      }
      this.searchLoading = false
    },
    // 测试下载链接
    async testDownloadLink(resource) {
      try {
        const res = await getUnifiedDownloadLink(resource.id)
        if (res.success) {
          this.currentLink = res.data.downloadUrl || ''
          this.currentShareCode = res.data.shareCode || ''
          const tianyiBaseUrl = process.env.VUE_APP_TIANYI_URL || 'http://localhost:4000'
          this.currentShareUrl = res.data.shareCode ? `${tianyiBaseUrl}/d/${res.data.shareCode}` : ''
          this.linkDialogVisible = true
        } else {
          this.$message.error(res.message || '获取下载链接失败')
        }
      } catch (e) {
        this.$message.error('获取下载链接失败')
      }
    },
    // 复制分享链接
    copyShareUrl(resource) {
      if (!resource.tianyi_share_code) {
        this.$message.warning('该资源没有分享码')
        return
      }
      const tianyiBaseUrl = process.env.VUE_APP_TIANYI_URL || 'http://localhost:4000'
      const url = `${tianyiBaseUrl}/d/${resource.tianyi_share_code}`
      navigator.clipboard.writeText(url)
      this.$message.success('永久下载链接已复制')
    },
    // 加载天翼云账号
    async loadTianyiAccounts() {
      try {
        const res = await getTianyiAccounts()
        if (res.success) {
          this.tianyiAccounts = res.data
          if (this.tianyiAccounts.length > 0) {
            this.selectedAccountId = this.tianyiAccounts[0].id
          }
        }
      } catch (e) {
        console.error('加载天翼云账号失败', e)
      }
    },
    // 加载天翼云文件
    async loadTianyiFiles() {
      if (!this.selectedAccountId) return
      this.filesLoading = true
      try {
        const res = await listTianyiFiles(this.selectedAccountId, { folderId: this.currentFolderId })
        if (res.success) {
          this.tianyiFiles = res.data
        } else {
          this.$message.error(res.message || '加载失败')
        }
      } catch (e) {
        this.$message.error('加载失败')
      }
      this.filesLoading = false
    },
    // 进入文件夹
    enterFolder(folder) {
      this.folderHistory.push(this.currentFolderId)
      this.currentFolderId = folder.id
      this.loadTianyiFiles()
    },
    // 返回上级
    goToParentFolder() {
      if (this.folderHistory.length > 0) {
        this.currentFolderId = this.folderHistory.pop()
        this.loadTianyiFiles()
      }
    },
    // 双击文件/文件夹
    handleFileDoubleClick(row) {
      if (row.isFolder) {
        this.enterFolder(row)
      } else {
        this.getFileLink(row)
      }
    },
    // 获取文件直链
    async getFileLink(file) {
      try {
        const res = await getTianyiDownloadLink(this.selectedAccountId, file.id, {
          fileName: file.name,
          fileSize: file.size
        })
        if (res.success) {
          this.currentLink = res.data.url || ''
          this.currentShareCode = res.data.shareCode || ''
          // 构建完整的永久直链地址
          const tianyiBaseUrl = process.env.VUE_APP_TIANYI_URL || 'http://localhost:4000'
          this.currentShareUrl = res.data.shareCode ? `${tianyiBaseUrl}/d/${res.data.shareCode}` : ''
          this.linkDialogVisible = true
        } else {
          this.$message.error(res.message || '获取直链失败')
        }
      } catch (e) {
        this.$message.error('获取直链失败')
      }
    },
    // 打开绑定对话框
    openBindDialog(resource) {
      this.bindForm.resourceId = resource.id
      this.bindForm.resourceTitle = resource.title
      this.bindForm.accountId = this.selectedAccountId
      this.selectedFiles = []
      this.bindFolderId = '-11'
      this.bindFolderHistory = []
      this.bindFiles = []
      this.bindFileSearchKeyword = '' // 清空搜索关键词
      this.bindDialogVisible = true
      // 自动加载文件列表
      if (this.bindForm.accountId) {
        this.loadBindFiles()
      }
    },
    // 加载绑定对话框内的文件列表
    async loadBindFiles() {
      if (!this.bindForm.accountId) return
      this.bindFilesLoading = true
      try {
        const res = await listTianyiFiles(this.bindForm.accountId, { folderId: this.bindFolderId })
        if (res.success) {
          this.bindFiles = res.data
        } else {
          this.$message.error(res.message || '加载失败')
        }
      } catch (e) {
        this.$message.error('加载失败')
      }
      this.bindFilesLoading = false
    },
    // 进入绑定对话框内的文件夹
    enterBindFolder(folder) {
      this.bindFolderHistory.push(this.bindFolderId)
      this.bindFolderId = folder.id
      this.loadBindFiles()
    },
    // 返回绑定对话框内的上级文件夹
    goToBindParentFolder() {
      if (this.bindFolderHistory.length > 0) {
        this.bindFolderId = this.bindFolderHistory.pop()
        this.loadBindFiles()
      }
    },
    // 双击绑定对话框内的文件/文件夹
    handleBindFileDoubleClick(row) {
      if (row.isFolder) {
        this.enterBindFolder(row)
      } else {
        this.selectBindFile(row)
      }
    },
    // 切换选择文件
    toggleSelectFile(file) {
      const index = this.selectedFiles.findIndex(f => f.fileId === file.id)
      if (index >= 0) {
        this.selectedFiles.splice(index, 1)
      } else {
        this.selectedFiles.push({
          fileId: file.id,
          fileName: file.name,
          fileSize: file.size || 0,
          label: '', // 自定义标签
          index: this.selectedFiles.length + 1
        })
      }
    },
    // 更新文件标签
    updateFileLabel(index, value) {
      if (this.selectedFiles[index]) {
        this.selectedFiles[index].label = value
      }
    },
    // 检查文件是否已选择
    isFileSelected(file) {
      return this.selectedFiles.some(f => f.fileId === file.id)
    },
    // 移除已选择的文件
    removeSelectedFile(index) {
      this.selectedFiles.splice(index, 1)
      // 重新排序 index
      this.selectedFiles.forEach((f, i) => { f.index = i + 1 })
    },
    // 清空已选择的文件
    clearSelectedFiles() {
      this.selectedFiles = []
    },
    // 获取账号名称
    getAccountName(accountId) {
      const account = this.tianyiAccounts.find(a => a.id === accountId)
      return account ? account.name : '-'
    },
    // 提交绑定（支持多文件）
    async submitBind() {
      if (!this.bindForm.accountId || this.selectedFiles.length === 0) {
        this.$message.warning('请选择要绑定的文件')
        return
      }
      this.bindLoading = true
      try {
        const res = await bindTianyiFile(this.bindForm.resourceId, {
          accountId: this.bindForm.accountId,
          files: this.selectedFiles
        })
        if (res.success) {
          this.$message.success(`绑定成功，共${res.data.count || this.selectedFiles.length}个文件`)
          this.bindDialogVisible = false
          this.loadPendingResources()
          this.loadBoundResources()
        } else {
          this.$message.error(res.message || '绑定失败')
        }
      } catch (e) {
        this.$message.error('绑定失败')
      }
      this.bindLoading = false
    },
    // 检查服务状态
    async checkServiceHealth() {
      try {
        const res = await tianyiHealthCheck()
        this.serviceStatus = {
          ok: res.success === true || res.code === 200,
          checkTime: new Date().toLocaleString()
        }
      } catch (e) {
        this.serviceStatus = {
          ok: false,
          checkTime: new Date().toLocaleString()
        }
      }
    },
    // 复制链接
    copyLink(text) {
      if (!text) {
        this.$message.warning('链接为空')
        return
      }
      navigator.clipboard.writeText(text)
      this.$message.success('已复制到剪贴板')
    },
    // 打开链接
    openLink(url) {
      if (!url) {
        this.$message.warning('链接为空')
        return
      }
      window.open(url, '_blank')
    },
    // 格式化文件大小
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let i = 0
      while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024
        i++
      }
      return bytes.toFixed(2) + ' ' + units[i]
    },
    // 存储类型标签
    getStorageTypeTag(type) {
      const map = { tianyi: 'success', '123pan': 'primary', direct: 'warning', none: 'info' }
      return map[type] || 'info'
    },
    getStorageTypeLabel(type) {
      const map = { tianyi: '天翼云', '123pan': '123网盘', direct: '直连', none: '无' }
      return map[type] || type
    },
    // 上传状态标签
    getUploadStatusTag(status) {
      const map = { pending: 'warning', uploading: 'primary', completed: 'success', failed: 'danger', manual: 'info' }
      return map[status] || 'info'
    },
    getUploadStatusLabel(status) {
      const map = { pending: '待上传', uploading: '上传中', completed: '已完成', failed: '失败', manual: '需手动' }
      return map[status] || status || '-'
    },
    // 打开自动上传对话框
    handleAutoUpload(resource) {
      // 检查是否有可用账号
      if (!this.tianyiAccounts || this.tianyiAccounts.length === 0) {
        this.$message.warning('没有可用的天翼云账号')
        return
      }
      
      this.autoUploadForm.resourceId = resource.id
      this.autoUploadForm.resourceTitle = resource.title
      this.autoUploadForm.accountId = this.selectedAccountId || this.tianyiAccounts[0].id
      this.autoUploadFolderId = '-11'
      this.autoUploadFolderHistory = []
      this.autoUploadFolderPath = []
      this.autoUploadFolders = []
      this.autoUploadDialogVisible = true
      
      // 加载文件夹列表
      if (this.autoUploadForm.accountId) {
        this.loadAutoUploadFolders()
      }
    },
    // 加载自动上传文件夹列表
    async loadAutoUploadFolders() {
      if (!this.autoUploadForm.accountId) return
      this.autoUploadFoldersLoading = true
      try {
        const res = await listTianyiFiles(this.autoUploadForm.accountId, { folderId: this.autoUploadFolderId })
        if (res.success) {
          // 只显示文件夹
          this.autoUploadFolders = res.data.filter(item => item.isFolder)
        } else {
          this.$message.error(res.message || '加载失败')
        }
      } catch (e) {
        this.$message.error('加载失败')
      }
      this.autoUploadFoldersLoading = false
    },
    // 进入文件夹
    enterAutoUploadFolder(folder) {
      this.autoUploadFolderHistory.push(this.autoUploadFolderId)
      this.autoUploadFolderPath.push({ id: folder.id, name: folder.name })
      this.autoUploadFolderId = folder.id
      this.loadAutoUploadFolders()
    },
    // 返回上级
    goToAutoUploadParent() {
      if (this.autoUploadFolderHistory.length > 0) {
        this.autoUploadFolderId = this.autoUploadFolderHistory.pop()
        this.autoUploadFolderPath.pop()
        this.loadAutoUploadFolders()
      }
    },
    // 返回根目录
    goToAutoUploadRoot() {
      this.autoUploadFolderId = '-11'
      this.autoUploadFolderHistory = []
      this.autoUploadFolderPath = []
      this.loadAutoUploadFolders()
    },
    // 提交自动上传
    async submitAutoUpload() {
      if (!this.autoUploadForm.accountId) {
        this.$message.warning('请选择天翼云账号')
        return
      }
      
      this.autoUploadLoading = true
      try {
        const res = await autoUploadToTianyi(this.autoUploadForm.resourceId, {
          accountId: this.autoUploadForm.accountId,
          folderId: this.autoUploadFolderId
        })
        
        if (res.success) {
          this.$message.success(res.data.message || '已加入上传队列')
          this.autoUploadDialogVisible = false
          // 刷新列表
          this.loadPendingResources()
        } else {
          this.$message.error(res.message || '自动上传失败')
        }
      } catch (e) {
        this.$message.error(e.message || '自动上传失败')
      }
      this.autoUploadLoading = false
    },
    // 重置上传状态
    async resetUploadStatus(resource) {
      try {
        await this.$confirm('确定要重置该资源的上传状态吗？', '提示', {
          type: 'warning'
        })
        const res = await resetResourceUploadStatus(resource.id)
        if (res.success) {
          this.$message.success('状态已重置')
          this.loadPendingResources()
        } else {
          this.$message.error(res.message || '重置失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('重置失败')
        }
      }
    },
    // 手动重试上传（针对已达到最大重试次数的任务）
    async retryUpload(resource) {
      try {
        await this.$confirm('确定要重试上传该资源吗？将重置重试次数', '提示', {
          type: 'warning'
        })
        const res = await resetResourceUploadStatus(resource.id)
        if (res.success) {
          this.$message.success('已加入上传队列，将自动重试')
          this.loadPendingResources()
        } else {
          this.$message.error(res.message || '重试失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('重试失败')
        }
      }
    },
    // 从待上传列表中移除（只重置状态，不删除游戏）
    async deleteResource(resource) {
      try {
        await this.$confirm(`确定要从待上传列表中移除 "${resource.title}" 吗？（不会删除游戏）`, '提示', {
          type: 'warning'
        })
        const res = await removeFromPendingList(resource.id)
        if (res.code === 200) {
          this.$message.success('已从待上传列表移除')
          this.loadPendingResources()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('操作失败')
        }
      }
    },
    // 显示任务进度
    async showTaskProgress(resource) {
      this.progressResourceId = resource.id
      this.progressData = {
        resourceTitle: resource.title,
        uploadStatus: resource.upload_status,
        tasks: []
      }
      this.progressDialogVisible = true
      await this.refreshTaskProgress()
    },
    // 刷新任务进度
    async refreshTaskProgress() {
      if (!this.progressResourceId) return
      this.progressLoading = true
      try {
        const res = await getResourceTaskProgress(this.progressResourceId)
        if (res.success) {
          this.progressData = res.data
          // 如果状态变化了，刷新列表
          if (res.data.uploadStatus === 'completed' || res.data.uploadStatus === 'failed') {
            this.loadPendingResources()
          }
        } else {
          this.$message.error(res.message || '获取进度失败')
        }
      } catch (e) {
        this.$message.error('获取进度失败')
      }
      this.progressLoading = false
    },
    // 任务状态标签
    getTaskStatusTag(status) {
      const map = { 
        pending: 'info', 
        downloading: 'primary', 
        uploading: 'warning', 
        getting_link: 'warning',
        completed: 'success', 
        failed: 'danger',
        cancelled: 'info',
        paused: 'info'
      }
      return map[status] || 'info'
    },
    getTaskStatusLabel(status) {
      const map = { 
        pending: '等待中', 
        downloading: '下载中', 
        uploading: '上传中', 
        getting_link: '获取直链',
        completed: '已完成', 
        failed: '失败',
        cancelled: '已取消',
        paused: '已暂停',
        unknown: '未知'
      }
      return map[status] || status || '-'
    }
  }
}
</script>

<style scoped>
.tianyi-management {
  padding: 20px;
}
.filter-bar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.total-info {
  margin-left: 20px;
  color: #666;
}
.link-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
.bind-dialog-content {
  display: flex;
  gap: 20px;
  min-height: 400px;
}
.bind-info-panel {
  width: 280px;
  flex-shrink: 0;
}
.bind-info-panel h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #303133;
}
.bind-file-browser {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.browser-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}
.file-search-bar {
  margin-bottom: 10px;
}
.file-search-bar .el-input {
  width: 100%;
}
.tianyi-info {
  font-size: 12px;
  line-height: 1.6;
  color: #606266;
}
/* 已选择文件列表样式 */
.selected-files-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 8px;
  background: #fafafa;
}
.selected-file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: white;
  border-radius: 4px;
  margin-bottom: 6px;
  border: 1px solid #e4e7ed;
}
.selected-file-item:last-child {
  margin-bottom: 0;
}
.file-index {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
}
.selected-file-item .file-info {
  flex: 1;
  min-width: 0;
}
.selected-file-item .file-name {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.selected-file-item .file-size {
  font-size: 11px;
  color: #909399;
}
.file-label-input {
  margin-bottom: 6px;
}
.file-label-input /deep/ .el-input__inner {
  height: 28px;
  font-size: 12px;
  padding: 0 8px;
}
/* 自动上传对话框样式 */
.auto-upload-content {
  min-height: 350px;
}
.folder-browser {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
}
.folder-path {
  flex: 1;
}
.folder-path /deep/ .el-breadcrumb__item {
  cursor: pointer;
}
.folder-path /deep/ .el-breadcrumb__inner:hover {
  color: #409eff;
}
.folder-tip {
  margin-top: 10px;
  padding: 8px 12px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  color: #1890ff;
  font-size: 13px;
}
/* 任务进度样式 */
.task-progress-item {
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 10px;
  background: #fafafa;
}
.task-progress-item:last-child {
  margin-bottom: 0;
}
.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.task-name {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
}
.task-info {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}
/* 操作按钮容器 */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.action-buttons .el-button {
  margin: 0 !important;
}
/* 重试次数样式 */
.retry-count {
  font-size: 11px;
  color: #f56c6c;
  margin-left: 4px;
  cursor: help;
}
</style>
